import { Info } from '@Components/Common/Info';
import {
  Button,
  ButtonGroup,
  Divider,
  Flex,
  Icon,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Space,
  Text,
} from '@tonic-one/react';
import { AngleLeftIcon, AngleRightIcon } from '@tonic-one/react-icons';
import { t } from 'i18next';
import React, { useEffect, useMemo, useRef, useState } from 'react';

const NUMBER = /^[1-9]\d*$/;

function MiniPagination(props: any) {
  const {
    totalCount = 0,
    pageSize,
    currentPage,
    changeCurrentPage,
    changePageSize,
    sizeOptions = [10, 50, 100],
    iconWithTip = false,
    tip = '',
  } = props;
  // ref
  const input: any = useRef();

  // states
  const [inputed, setInputed] = useState('1');
  const [inputting, setInputting] = useState(false);

  // cached states
  const totalPages = useMemo(() => {
    return Math.ceil(totalCount / pageSize) || 1;
  }, [totalCount, pageSize]);

  const prevPageDisabled = useMemo(() => {
    if (inputting) return true;
    return currentPage <= 1;
  }, [currentPage, inputting]);

  const nextPageDisabled = useMemo(() => {
    if (inputting) return true;
    return currentPage >= totalPages;
  }, [currentPage, totalPages, inputting]);

  // effects
  useEffect(() => {
    setInputed(`${currentPage}`);
  }, [currentPage]);

  // actions

  const onInputChange = (e: any) => {
    const value = e.target.value;
    if (NUMBER.test(value)) {
      setInputed(value);
    } else if (!value) {
      setInputed('');
    }
  };

  const inputDone = () => {
    if (NUMBER.test(inputed)) {
      const value = parseInt(inputed, 10);
      const minValue = value > totalPages ? totalPages : value;
      changeCurrentPage(minValue);
      setInputed(`${minValue}`);
    } else {
      changeCurrentPage(1);
    }
  };

  const handleKeyPress = (e: any) => {
    if (e.which === 13 || e.key === 'Enter') {
      inputDone();
      input?.current?.blur();
    }
  };

  const handleBlur = () => {
    setInputting(false);
    if (NUMBER.test(inputed) && currentPage === parseInt(inputed)) return;
    inputDone();
  };

  // actions
  const onChangeSize = (e: any) => {
    const pageSize = parseInt(e.target.innerHTML);
    changePageSize(pageSize || sizeOptions[0]);
  };

  return (
    <Flex>
      <Flex mr="3x" alignItems="center">
        <Text>
          {t('reportCommon.labels.total')}:&nbsp;{totalCount}
        </Text>
        {iconWithTip && (
          <>
            <Space width="2x"></Space>
            <Info content={tip} />
          </>
        )}
        <Divider orientation="vertical" height="6x" ml="3x" />
        <Menu placement="top-start">
          <MenuButton variant="ghost">
            {pageSize}&nbsp;{t('reportCommon.labels.perPage')}
          </MenuButton>
          <MenuList onClick={onChangeSize}>
            {sizeOptions.map((s: any) => (
              <MenuItem value={s} key={s}>
                {s}&nbsp;{t('reportCommon.labels.perPage')}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
        <Input
          ref={input}
          width={32}
          px={0}
          textAlign="center"
          onKeyPress={handleKeyPress}
          onFocus={() => {
            setInputting(true);
          }}
          onBlur={handleBlur}
          onChange={onInputChange}
          value={inputed}
        />
        <Space width="2x" />
        <Text>/</Text>
        <Space width="2x" />
        <Text>{totalPages}</Text>
      </Flex>
      <ButtonGroup
        variant="secondary"
        css={{
          '> *:not(:first-of-type)': {
            marginLeft: -1,
          },
        }}
      >
        <Button
          disabled={prevPageDisabled}
          onClick={() => {
            if (NUMBER.test(inputed) && parseInt(inputed) !== currentPage) {
              return;
            }
            const prevPage = currentPage > 1 ? currentPage - 1 : currentPage;
            if (prevPage !== currentPage) {
              changeCurrentPage(prevPage);
            }
          }}
        >
          <Icon as={AngleLeftIcon} />
        </Button>
        <Button
          disabled={nextPageDisabled}
          onClick={() => {
            if (NUMBER.test(inputed) && parseInt(inputed) !== currentPage) {
              return;
            }
            const nextPage =
              currentPage < totalPages ? currentPage + 1 : currentPage;
            if (nextPage !== currentPage) {
              changeCurrentPage(nextPage);
            }
          }}
        >
          <Icon as={AngleRightIcon} />
        </Button>
      </ButtonGroup>
    </Flex>
  );
}

export default MiniPagination;
